import { Copies } from "../../../hooks/useCopies";
import { AssetConfig, AssetUploadType, DimensionType } from "./types";
import { getKBInMB, isEmpty } from "../../../utils";
import { SizeType } from "../../fields/imageUploadField/enum";
import {
  ACCEPTED_FILE_MIME_TYPE,
  ASPECT_RATIO_ERROR_MARGIN,
  MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB
} from "../../../utils/constants";
import I18n from "shared/types/I18n";

export function getAssetUploadRecommendedCopy({
  assetUploadType = AssetUploadType.IMAGE,
  copies,
  config,
  min,
  max,
  customCopy,
  customCopyKey
}: {
  copies: Copies;
  config?: AssetConfig;
  assetUploadType?: AssetUploadType;
  min?: number;
  max?: number;
  customCopy?: string;
  customCopyKey?: keyof I18n;
}) {
  switch (assetUploadType) {
    case AssetUploadType.FILE:
      const copiesArr = [
        customCopy ||
          copies.get(customCopyKey || "file.upload.recommended.msg", {
            0: ACCEPTED_FILE_MIME_TYPE,
            1: getMaxSizeDisplayText(config?.maxSize)
          }),
        getMinimumDisplayText(copies, min),
        getMaximumDisplayText(copies, max)
      ];
      return joinCopies(copiesArr);
    case AssetUploadType.IMAGE:
      return (
        customCopy ||
        copies.get(customCopyKey || "image.dimension.recommended.msg", {
          0: config?.aspectRatio,
          1: `${config?.dimension.width} x ${config?.dimension.height}`,
          2: getMaxSizeDisplayText(config?.maxSize)
        })
      );
    case AssetUploadType.BOTH:
      return (
        customCopy ||
        copies.get(customCopyKey || "image.and.file.upload.recommended.msg", {
          0: config?.aspectRatio,
          1: `${config?.dimension.width} x ${config?.dimension.height}`,
          2: getMaxSizeDisplayText(config?.maxSize)
        })
      );
    default:
      return "";
  }
}

export function getMaxSizeDisplayText(maxSize: number = MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB) {
  const sizeInMB = getKBInMB(maxSize);
  return sizeInMB >= 1 ? `${sizeInMB} ${SizeType.MB}` : `${maxSize} ${SizeType.KB}`;
}

function joinCopies(copiesArr: Array<string>) {
  return copiesArr.filter(copy => !isEmpty(copy)).join(", ");
}

function getMinimumDisplayText(copies: Copies, min?: number) {
  return min ? copies.get("min.count", { 0: min }) : "";
}

function getMaximumDisplayText(copies: Copies, max?: number) {
  return max ? copies.get("max.count", { 0: max }) : "";
}

export function isInValidAspectRatio({
  fileDimension,
  recommendedDimension
}: {
  fileDimension: DimensionType;
  recommendedDimension: DimensionType;
}) {
  const uploadedFileAspectRatio = fileDimension.width / fileDimension.height;
  const recommendedAspectRatio = recommendedDimension.width / recommendedDimension.height;
  return Math.abs(uploadedFileAspectRatio - recommendedAspectRatio) > ASPECT_RATIO_ERROR_MARGIN;
}

export function isInValidDimension({
  fileDimension,
  recommendedDimension
}: {
  fileDimension: DimensionType;
  recommendedDimension: DimensionType;
}) {
  return fileDimension.width !== recommendedDimension.width || fileDimension.height !== recommendedDimension.height;
}

export function isInvalidMinWidthAndMaxHeight({
  fileDimension,
  recommendedDimension
}: {
  fileDimension: DimensionType;
  recommendedDimension: DimensionType;
}) {
  if (
    !recommendedDimension.minWidth ||
    !recommendedDimension.maxHeight ||
    typeof recommendedDimension.maxHeight === "string"
  )
    return false;

  return fileDimension.width < recommendedDimension.minWidth || fileDimension.height > recommendedDimension.maxHeight;
}

export function isInvalidMaxWidth({
  fileDimension,
  recommendedDimension
}: {
  fileDimension: Pick<DimensionType, "width">;
  recommendedDimension: DimensionType;
}) {
  if (!recommendedDimension.maxWidth) return false;

  return fileDimension.width > recommendedDimension.maxWidth;
}
